import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Combines class names using clsx and tailwind-merge
 * This utility merges Tailwind classes intelligently,
 * resolving conflicts (e.g., p-4 + p-2 = p-2)
 *
 * @param  {...any} inputs - Class names, arrays, or objects
 * @returns {string} - Merged class string
 *
 * @example
 * cn('px-4 py-2', condition && 'bg-primary', 'text-white')
 * cn({ 'opacity-50': disabled }, className)
 */
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Date formatting utilities for consistent date display across the app
 */

/**
 * Format a date string to a short, compact format
 * @param {string} dateString - Date in YYYY-MM-DD or ISO format
 * @returns {string} - Formatted date (e.g., "Nov 26", "Today", "Yesterday")
 */
export function formatDateShort(dateString) {
  if (!dateString) return '-';

  const date = new Date(dateString.includes('T') ? dateString : dateString + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.getTime() === today.getTime()) {
    return 'Today';
  }
  if (date.getTime() === yesterday.getTime()) {
    return 'Yesterday';
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format a date string to a medium format with year
 * @param {string} dateString - Date in YYYY-MM-DD or ISO format
 * @returns {string} - Formatted date (e.g., "Nov 26, 2024")
 */
export function formatDateMedium(dateString) {
  if (!dateString) return '-';

  const date = new Date(dateString.includes('T') ? dateString : dateString + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a date string to a long, detailed format
 * @param {string} dateString - Date in YYYY-MM-DD or ISO format
 * @returns {string} - Formatted date (e.g., "Tuesday, November 26, 2024")
 */
export function formatDateLong(dateString) {
  if (!dateString) return '-';

  const date = new Date(dateString.includes('T') ? dateString : dateString + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Format a timestamp to relative time (e.g., "2 hours ago", "3 days ago")
 * @param {string|number} timestamp - ISO timestamp string or Unix timestamp
 * @returns {string} - Relative time string
 */
export function formatRelativeTime(timestamp) {
  if (!timestamp) return '-';

  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  if (diffSecs < 60) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffWeeks < 4) return `${diffWeeks}w ago`;
  if (diffMonths < 12) return `${diffMonths}mo ago`;

  return formatDateMedium(timestamp);
}

/**
 * Format a date for display in a table or list
 * Shows compact format with full date in title attribute
 * @param {string} dateString - Date in YYYY-MM-DD or ISO format
 * @returns {Object} - { display: string, title: string }
 */
export function formatDateForDisplay(dateString) {
  return {
    display: formatDateShort(dateString),
    title: formatDateLong(dateString),
  };
}

/**
 * Staleness detection utilities for static holdings
 *
 * Holdings that require manual updates should show a warning when they become stale.
 * Different asset types have different staleness thresholds:
 * - Cash holdings: 30 days (1 month)
 * - Real Estate, Vehicles, Liabilities: 365 days (1 year)
 */

// Asset types that require staleness checking
const STALENESS_THRESHOLDS = {
  // Cash holdings - should be updated monthly
  'Cash/Bank Account': 30,
  // Real estate - should be updated yearly
  'Real Estate': 365,
  'Real Estate & Property': 365,
  // Vehicles and equipment - should be updated yearly
  'Vehicle': 365,
  'Equipment': 365,
  'Other High-Value Asset': 365,
  // Liabilities - should be updated yearly
  'Mortgage': 365,
  'Loan': 365,
  'Credit Card Debt': 365,
  'Other Liability': 365,
};

/**
 * Get staleness information for a holding based on its type and last update
 *
 * @param {Object} holding - The holding object
 * @param {string} holding.assetType - The type of asset
 * @param {string} holding.lastManualUpdate - ISO timestamp of last update
 * @param {boolean} holding.useManualValue - Whether this is a manual-value holding
 * @returns {Object|null} - Staleness info or null if not stale
 *   - isStale: boolean
 *   - daysSinceUpdate: number
 *   - threshold: number (days)
 *   - severity: 'warning' | 'critical'
 *   - message: string
 */
export function getHoldingStaleness(holding) {
  // Only check manual value holdings
  if (!holding?.useManualValue) {
    return null;
  }

  const threshold = STALENESS_THRESHOLDS[holding.assetType];

  // If no threshold defined for this asset type, it doesn't need staleness checking
  if (!threshold) {
    return null;
  }

  const lastUpdate = holding.lastManualUpdate;
  if (!lastUpdate) {
    // No update date - consider it stale if it's a manual asset
    return {
      isStale: true,
      daysSinceUpdate: null,
      threshold,
      severity: 'warning',
      message: 'No update date recorded',
    };
  }

  const lastUpdateDate = new Date(lastUpdate);
  const now = new Date();
  const daysSinceUpdate = Math.floor((now - lastUpdateDate) / (1000 * 60 * 60 * 24));

  if (daysSinceUpdate < threshold) {
    return null; // Not stale
  }

  // Determine severity based on how far past the threshold
  // Critical if more than 50% past the threshold
  const severity = daysSinceUpdate > threshold * 1.5 ? 'critical' : 'warning';

  // Create human-readable message
  let timeAgo;
  if (daysSinceUpdate >= 365) {
    const years = Math.floor(daysSinceUpdate / 365);
    timeAgo = years === 1 ? '1 year' : `${years} years`;
  } else if (daysSinceUpdate >= 30) {
    const months = Math.floor(daysSinceUpdate / 30);
    timeAgo = months === 1 ? '1 month' : `${months} months`;
  } else {
    timeAgo = `${daysSinceUpdate} days`;
  }

  const thresholdLabel = threshold >= 365
    ? `${Math.floor(threshold / 365)} year`
    : `${threshold} days`;

  return {
    isStale: true,
    daysSinceUpdate,
    threshold,
    severity,
    message: `Last updated ${timeAgo} ago (recommended: every ${thresholdLabel})`,
  };
}

/**
 * Check if a holding type requires staleness tracking
 * @param {string} assetType - The asset type
 * @returns {boolean} - Whether this type should be tracked for staleness
 */
export function isStaleTrackableType(assetType) {
  return assetType in STALENESS_THRESHOLDS;
}

/**
 * Get the staleness threshold for an asset type
 * @param {string} assetType - The asset type
 * @returns {number|null} - Days until considered stale, or null if not tracked
 */
export function getStalenessThreshold(assetType) {
  return STALENESS_THRESHOLDS[assetType] || null;
}
